import React, { useState, useEffect } from 'react';
import { ptvData } from '../data/rtogData';
import { interpolate } from '../utils/helpers';
import InputField from '../components/InputField';
import OutputCard from '../components/OutputCard';

const RTOGCalculator = ({ useInstitutionalData, setUseInstitutionalData, institutionalData }) => {
  const [inputs, setInputs] = useState({ ptvVolume: '', prescriptionDose: '' });
  const [outputs, setOutputs] = useState({ r50p: '-', r50v: '-', d2cmp: '-', d2cmv: '-', scorecardCI50: '-', ptv15Percent: '-', scorecardBody: '-', dose105Percent: '-', dose50Percent: '-' });
  const [table, setTable] = useState(ptvData);
  const [status, setStatus] = useState('');

  const handleInputChange = (field, value) => {
    setInputs(prev => ({...prev, [field]: value}));
  };

  useEffect(() => {
    const { ptvVolume, prescriptionDose } = inputs;
    const ptv = parseFloat(ptvVolume);
    const dose = parseFloat(prescriptionDose);
    setStatus('');
    
    const sourceTable = useInstitutionalData && institutionalData?.customTable ? institutionalData.customTable : ptvData;

    if (isNaN(ptv)) {
        setOutputs({ r50p: '-', r50v: '-', d2cmp: '-', d2cmv: '-', scorecardCI50: '-', ptv15Percent: '-', scorecardBody: '-', dose105Percent: '-', dose50Percent: '-' });
        setTable(sourceTable || ptvData);
        return;
    }

    let calculatedValues = null;
    let upperPointIndex = -1;

    const dataSet = sourceTable.map(d => ({...d, ptv: parseFloat(d.ptv)})).sort((a,b) => a.ptv - b.ptv);
    let lowerPoint = null; 
    let upperPoint = null;
    for (let i = 0; i < dataSet.length - 1; i++) {
        if (ptv >= dataSet[i].ptv && ptv <= dataSet[i + 1].ptv) {
            lowerPoint = dataSet[i];
            upperPoint = dataSet[i + 1];
            upperPointIndex = i + 1;
            break;
        }
    }
    if (lowerPoint && upperPoint) {
        calculatedValues = { 
            r50p: interpolate(ptv, lowerPoint.ptv, parseFloat(lowerPoint.r50p), upperPoint.ptv, parseFloat(upperPoint.r50p)), 
            r50v: interpolate(ptv, lowerPoint.ptv, parseFloat(lowerPoint.r50v), upperPoint.ptv, parseFloat(upperPoint.r50v)), 
            d2cmp: interpolate(ptv, lowerPoint.ptv, parseFloat(lowerPoint.d2cmp), upperPoint.ptv, parseFloat(upperPoint.d2cmp)), 
            d2cmv: interpolate(ptv, lowerPoint.ptv, parseFloat(lowerPoint.d2cmv), upperPoint.ptv, parseFloat(upperPoint.d2cmv)) 
        };
    } else {
        setStatus('PTV volume is outside the available data range.');
    }
    
    if (calculatedValues) {
        const scorecardCI50 = (calculatedValues.r50p || 0) * ptv;
        const ptv15Percent = ptv * 0.15;
        const newOutputs = {
            r50p: (calculatedValues.r50p || 0).toFixed(2),
            r50v: (calculatedValues.r50v || 0).toFixed(2),
            d2cmp: (calculatedValues.d2cmp || 0).toFixed(2),
            d2cmv: (calculatedValues.d2cmv || 0).toFixed(2),
            scorecardCI50: scorecardCI50.toFixed(2),
            ptv15Percent: ptv15Percent.toFixed(2),
            scorecardBody: isNaN(dose) ? '-' : ((calculatedValues.d2cmp / 100) * dose).toFixed(2),
            dose105Percent: isNaN(dose) ? '-' : (dose * 1.05).toFixed(2),
            dose50Percent: isNaN(dose) ? '-' : (dose * 0.50).toFixed(2),
        };
        setOutputs(newOutputs);

        if (upperPointIndex !== -1 && (!useInstitutionalData || institutionalData?.dataType === 'points')) {
            const interpolatedRow = { 
                ptv: ptv.toFixed(1),
                r50p: newOutputs.r50p, r50v: newOutputs.r50v,
                d2cmp: newOutputs.d2cmp, d2cmv: newOutputs.d2cmv,
                interpolated: true 
            };
            const newTable = [...sourceTable];
            newTable.splice(upperPointIndex, 0, interpolatedRow);
            setTable(newTable);
        } else {
            setTable(sourceTable);
        }
    } else {
        setOutputs({ r50p: '-', r50v: '-', d2cmp: '-', d2cmv: '-', scorecardCI50: '-', ptv15Percent: '-', scorecardBody: '-', dose105Percent: '-', dose50Percent: '-' });
        setTable(sourceTable);
    }
  }, [inputs, useInstitutionalData, institutionalData]);

  return (
    <div>
      <div className="text-center mb-8">
        <h1 className="text-2xl md:text-4xl font-bold text-gray-900 dark:text-cyan-400 mb-2">SABR Conformity Index Calculator</h1>
        <div className="mt-4 mb-8 max-w-3xl mx-auto">
            <p className="text-md text-gray-700 dark:text-gray-300 bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg border border-blue-200 dark:border-blue-800">
                This tool helps radiation therapists, radiation oncologists and medical physicists evaluate SABR lung plans against RTOG 0815 conformity criteria. Use the calculators below, or explore the education modules for tutorials and competency quizzes.
            </p>
        </div>
        <p className="text-gray-600 dark:text-gray-400 text-base md:text-lg">Calculate R50% and D2cm values based on RTOG 0815 Guidelines</p>
      </div>

      <div className="flex items-center justify-center mb-6">
          <label className="flex items-center text-gray-700 dark:text-gray-300 cursor-pointer">
              <input type="checkbox" checked={useInstitutionalData} onChange={() => setUseInstitutionalData(!useInstitutionalData)} className="form-checkbox h-5 w-5 text-blue-600" />
              <span className="ml-2 font-semibold">Use My Institutional Data</span>
          </label>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
        <InputField label="Enter PTV Volume (cc)" value={inputs.ptvVolume} onChange={e => handleInputChange('ptvVolume', e.target.value)} type="number" step="0.1" placeholder="e.g., 85" />
        <InputField label="Enter Total Dose (Gy)" value={inputs.prescriptionDose} onChange={e => handleInputChange('prescriptionDose', e.target.value)} type="number" step="0.1" placeholder="e.g., 60" />
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6 mb-8">
        <OutputCard label="R50% Per Protocol" value={outputs.r50p} colour="blue" />
        <OutputCard label="R50% Acceptable Variation" value={outputs.r50v} colour="teal" />
        <OutputCard label="D2cm[%] Per Protocol" value={outputs.d2cmp} colour="green" />
        <OutputCard label="D2cm[%] Acceptable Variation" value={outputs.d2cmv} colour="cyan" />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        <div className="bg-purple-100 dark:bg-purple-900/20 rounded-2xl p-4 md:p-6 shadow-lg border-2 border-purple-300 dark:border-purple-600 col-span-1">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <OutputCard label="Scorecard CI50" value={outputs.scorecardCI50} colour="purple" simple />
                <OutputCard label="50% of Prescription Dose (Gy)" value={outputs.dose50Percent} colour="purple" simple />
            </div>
        </div>
        <div className="bg-indigo-100 dark:bg-indigo-900/20 rounded-2xl p-4 md:p-6 shadow-lg border-2 border-indigo-300 dark:border-indigo-600 col-span-1">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <OutputCard label="15% PTV Vol (cc)" value={outputs.ptv15Percent} colour="indigo" simple />
                <OutputCard label="105% of Prescription Dose (Gy)" value={outputs.dose105Percent} colour="indigo" simple />
            </div>
        </div>
        <div className="bg-pink-100 dark:bg-pink-900/20 rounded-2xl p-4 md:p-6 shadow-lg border-2 border-pink-300 dark:border-pink-600 col-span-1 md:col-span-2 lg:col-span-1">
            <h3 className="text-sm md:text-base text-pink-600 dark:text-pink-300 font-semibold mb-2 text-center leading-tight">Scorecard Body<br/>(PTV+2cm) (Gy)</h3>
            <p className="text-xl md:text-2xl font-bold text-pink-800 dark:text-pink-400 text-center">{outputs.scorecardBody}</p>
        </div>
      </div>

      {status && <div className="mt-8 text-center text-md md:text-lg font-medium text-red-500">{status}</div>}
      <div className="mt-12">
        <h2 className="text-xl md:text-3xl font-bold text-gray-900 dark:text-cyan-400 mb-4 text-center">{useInstitutionalData ? 'My Institutional Data' : 'RTOG 0815 Data Table'}</h2>
        <div className="overflow-x-auto">
            <table className="table-auto w-full text-left rounded-lg overflow-hidden border border-gray-300 dark:border-gray-700">
                <thead className="bg-gray-200 dark:bg-gray-800">
                    <tr>
                        <th className="px-4 py-3 border-r border-gray-300 dark:border-gray-700 text-sm md:text-base font-semibold text-gray-700 dark:text-gray-300">PTV Volume (cc)</th>
                        <th className="px-4 py-3 border-r border-gray-300 dark:border-gray-700 text-sm md:text-base font-semibold text-gray-700 dark:text-gray-300">R50% Per Protocol</th>
                        <th className="px-4 py-3 border-r border-gray-300 dark:border-gray-700 text-sm md:text-base font-semibold text-gray-700 dark:text-gray-300">R50% Acceptable Variation</th>
                        <th className="px-4 py-3 border-r border-gray-300 dark:border-gray-700 text-sm md:text-base font-semibold text-gray-700 dark:text-gray-300">D2cm[%] Per Protocol</th>
                        <th className="px-4 py-3 text-sm md:text-base font-semibold text-gray-700 dark:text-gray-300">D2cm[%] Acceptable Variation</th>
                    </tr>
                </thead>
                <tbody className="bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-800">
                    {(table || []).map((row, index) => (
                        <tr key={index} className={`hover:bg-gray-100 dark:hover:bg-gray-800 transition duration-150 ${row.interpolated ? 'bg-blue-100 dark:bg-blue-900/30' : ''}`}>
                            <td className="px-4 py-2 border-r border-gray-200 dark:border-gray-800">{row.ptv}</td>
                            <td className="px-4 py-2 border-r border-gray-200 dark:border-gray-800">{row.r50p}</td>
                            <td className="px-4 py-2 border-r border-gray-200 dark:border-gray-800">{row.r50v}</td>
                            <td className="px-4 py-2 border-r border-gray-200 dark:border-gray-800">{row.d2cmp}</td>
                            <td className="px-4 py-2">{row.d2cmv}</td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
      </div>
    </div>
  );
};

export default RTOGCalculator;
